import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Form, Space, Spin } from 'antd'
import { ArrowLeft } from 'lucide-react'
import { FormAnchorNav } from '@/components/form/form-anchor-nav.tsx'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { BusinessLicenseOcrModal } from './business-license-ocr-modal.tsx'
import { LegalEntityFormFields } from './legal-entity-form-fields.tsx'
import {
  canRecognizeBusinessLicense,
  getInitialBusinessLicense,
  hasBusinessLicenseOcrData,
  INITIAL_LEGAL_ENTITY_VALUES,
  LEGAL_ENTITY_FORM_SECTIONS,
  toLegalEntityFormValues,
  type BusinessLicenseOcrCandidate,
  type LegalEntityFormValues,
  type OrganizationDetail
} from './legal-entity-form.model.ts'
import type { OrganizationListSearch } from './legal-entity-list-search.ts'

export function LegalEntityForm({
  organizationId,
  returnSearch
}: {
  organizationId?: string
  returnSearch?: OrganizationListSearch
}) {
  const access = useAccess()
  const canManageAccounts = access.all([
    'organization.account.read',
    'organization.account.create',
    'organization.account.update',
    'organization.account.status.update'
  ])
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [form] = Form.useForm<LegalEntityFormValues>()
  const isLongTerm = Form.useWatch('isLongTerm', form)
  const hydratedId = useRef<string | undefined>(undefined)
  const ocrRequestId = useRef(0)
  const [licenseSource, setLicenseSource] = useState<{ file: File; fileId: number }>()
  const [ocrCandidate, setOcrCandidate] = useState<BusinessLicenseOcrCandidate>()
  const numericOrganizationId = organizationId ? Number(organizationId) : undefined
  const detail = useQuery({
    ...trpc.organization.detail.queryOptions(numericOrganizationId ?? 0),
    enabled: numericOrganizationId !== undefined
  })
  const upsert = useMutation(trpc.organization.upsert.mutationOptions())
  const recognize = useMutation(trpc.ocr.businessLicense.mutationOptions())
  const deleteCacheFile = useMutation(trpc.file.deleteCache.mutationOptions())
  const organization = detail.data as OrganizationDetail | undefined

  useEffect(() => {
    if (!organizationId) {
      form.setFieldsValue(INITIAL_LEGAL_ENTITY_VALUES)
      return
    }
    if (!organization || hydratedId.current === organizationId) {
      return
    }
    form.setFieldsValue(toLegalEntityFormValues(organization))
    hydratedId.current = organizationId
  }, [form, organization, organizationId])

  const goBack = () =>
    navigate({ to: '/master-data/legal-entities', search: returnSearch ?? { current: 1, pageSize: 20 } })

  const save = async (values: LegalEntityFormValues) => {
    const businessLicenseFileId = values.businessLicenseFileIds[0]
    if (!businessLicenseFileId) {
      form.setFields([{ errors: ['请上传营业执照'], name: 'businessLicenseFileIds' }])
      return
    }
    try {
      await upsert.mutateAsync({
        id: numericOrganizationId,
        data: {
          accounts: canManageAccounts ? values.accounts : undefined,
          businessLicenseFileId,
          businessScope: values.businessScope?.trim() || undefined,
          companyType: values.companyType?.trim() || undefined,
          establishedOn: values.establishedOn || undefined,
          isLongTerm: values.isLongTerm,
          legalRepresentative: values.legalRepresentative?.trim() || null,
          legalRepresentativeIdNumber: values.legalRepresentativeIdNumber?.trim().toUpperCase() || null,
          name: values.name.trim(),
          note: values.note?.trim() || undefined,
          operationMode: values.operationMode,
          registeredAddress: values.registeredAddress.trim(),
          registeredCapital: values.registeredCapital?.trim() || undefined,
          unifiedSocialCreditCode: values.unifiedSocialCreditCode.trim().toUpperCase(),
          validUntil: values.isLongTerm ? undefined : values.validUntil || undefined
        }
      })
      void message.success(organizationId ? '经营主体已更新' : '经营主体已创建')
      await queryClient.invalidateQueries(trpc.organization.list.queryFilter())
      if (numericOrganizationId !== undefined) {
        queryClient.removeQueries(trpc.organization.detail.queryFilter(numericOrganizationId))
      }
      await goBack()
    } catch (error) {
      void message.error(getErrorMessage(error, '经营主体保存失败'))
    }
  }

  const clearOcrState = () => {
    ++ocrRequestId.current
    setLicenseSource(undefined)
    setOcrCandidate(undefined)
  }

  const removeFailedLicense = async (fileId: number, reason: string) => {
    const currentFileIds = form.getFieldValue('businessLicenseFileIds') as number[] | undefined
    if (!currentFileIds?.includes(fileId)) {
      clearOcrState()
      return
    }
    try {
      await deleteCacheFile.mutateAsync({ id: fileId })
      form.setFieldValue('businessLicenseFileIds', [])
      clearOcrState()
      void message.error(`${reason}，图片已删除，请重新上传清晰原件`)
    } catch (error) {
      const remainingFileIds = form.getFieldValue('businessLicenseFileIds') as number[] | undefined
      if (!remainingFileIds?.includes(fileId)) {
        clearOcrState()
        return
      }
      setLicenseSource(undefined)
      void message.error(
        `${reason}；图片自动删除失败，请手动删除图片后重新上传（${getErrorMessage(error, '删除服务暂时不可用')}）`
      )
    }
  }

  const recognizeBusinessLicense = async (file: File, fileId: number) => {
    const requestId = ++ocrRequestId.current
    const formData = new FormData()
    formData.set('file', file)
    try {
      const result = await recognize.mutateAsync(formData)
      if (requestId !== ocrRequestId.current) {
        return
      }
      if (!hasBusinessLicenseOcrData(result)) {
        await removeFailedLicense(fileId, '未识别到有效的营业执照信息')
        return
      }
      setOcrCandidate(result)
    } catch (error) {
      if (requestId !== ocrRequestId.current) {
        return
      }
      await removeFailedLicense(fileId, getErrorMessage(error, '营业执照识别失败'))
    }
  }

  const runOcr = () => {
    if (!licenseSource) {
      void message.info('请先重新选择一份营业执照图片，再进行 OCR 识别')
      return
    }
    void recognizeBusinessLicense(licenseSource.file, licenseSource.fileId)
  }

  const handleLicenseUploaded = (file: File, uploadedFile: { id: number }) => {
    setOcrCandidate(undefined)
    if (!canRecognizeBusinessLicense(file)) {
      ++ocrRequestId.current
      setLicenseSource(undefined)
      void message.info('文件已上传；OCR 仅支持 JPEG、PNG、BMP 图片，请手工填写工商信息')
      return
    }
    setLicenseSource({ file, fileId: uploadedFile.id })
    void recognizeBusinessLicense(file, uploadedFile.id)
  }

  const applyOcrCandidate = () => {
    if (!ocrCandidate) {
      return
    }
    form.setFieldsValue({
      ...(ocrCandidate.name ? { name: ocrCandidate.name } : {}),
      ...(ocrCandidate.unifiedSocialCreditCode
        ? { unifiedSocialCreditCode: ocrCandidate.unifiedSocialCreditCode }
        : {}),
      ...(ocrCandidate.legalRepresentative ? { legalRepresentative: ocrCandidate.legalRepresentative } : {}),
      ...(ocrCandidate.registeredAddress ? { registeredAddress: ocrCandidate.registeredAddress } : {}),
      ...(ocrCandidate.companyType ? { companyType: ocrCandidate.companyType } : {}),
      ...(ocrCandidate.registeredCapital ? { registeredCapital: ocrCandidate.registeredCapital } : {}),
      ...(ocrCandidate.establishedOn ? { establishedOn: ocrCandidate.establishedOn } : {}),
      ...(ocrCandidate.businessScope ? { businessScope: ocrCandidate.businessScope } : {}),
      isLongTerm: ocrCandidate.isLongTerm,
      validUntil: ocrCandidate.isLongTerm ? undefined : (ocrCandidate.validUntil ?? undefined)
    })
    setOcrCandidate(undefined)
    void message.success('已应用 OCR 候选信息，请核对后保存')
  }

  const restore = () => {
    form.resetFields()
    form.setFieldsValue(organization ? toLegalEntityFormValues(organization) : INITIAL_LEGAL_ENTITY_VALUES)
    clearOcrState()
  }

  if (organizationId && detail.isLoading) {
    return (
      <FormPageContainer breadcrumbRender={false} title="编辑经营主体">
        <div className="flex min-h-72 items-center justify-center">
          <Spin description="正在读取主体资料…" />
        </div>
      </FormPageContainer>
    )
  }

  if (organizationId && detail.isError) {
    return (
      <FormPageContainer breadcrumbRender={false} title="编辑经营主体">
        <Alert
          action={<Button onClick={() => void detail.refetch()}>重试</Button>}
          showIcon
          title={getErrorMessage(detail.error, '主体详情加载失败')}
          type="error"
        />
      </FormPageContainer>
    )
  }

  return (
    <FormPageContainer
      breadcrumbRender={false}
      content="上传营业执照图片后将自动 OCR 预填；识别结果需确认，识别失败会删除本次图片，但仍可手工填写。"
      title={organizationId ? `编辑主体 · ${organization?.name ?? ''}` : '新建经营主体'}
    >
      <Form<LegalEntityFormValues>
        form={form}
        initialValues={INITIAL_LEGAL_ENTITY_VALUES}
        layout="vertical"
        onFinish={(values) => void save(values)}
        scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
      >
        <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col">
            <LegalEntityFormFields
              canManageAccounts={canManageAccounts}
              form={form}
              initialLicense={getInitialBusinessLicense(organization)}
              isLongTerm={isLongTerm}
              licenseSourceFile={licenseSource?.file}
              onFilesChange={(fileIds) => {
                if (fileIds.length === 0) {
                  clearOcrState()
                }
              }}
              onLicenseUploaded={handleLicenseUploaded}
              onRunOcr={runOcr}
              recognizePending={recognize.isPending}
            />

            <div className="border-border bg-surface/95 sticky bottom-3 z-10 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 shadow-xs backdrop-blur">
              <Button icon={<ArrowLeft aria-hidden size={16} />} onClick={() => void goBack()}>
                返回列表
              </Button>
              <Space>
                <Button disabled={upsert.isPending} onClick={restore}>
                  {organizationId ? '恢复已保存内容' : '重置'}
                </Button>
                <Button htmlType="submit" loading={upsert.isPending} type="primary">
                  保存主体
                </Button>
              </Space>
            </div>
          </div>

          <FormAnchorNav className="hidden lg:block" sections={LEGAL_ENTITY_FORM_SECTIONS} />
        </div>
      </Form>

      <BusinessLicenseOcrModal
        candidate={ocrCandidate}
        onApply={applyOcrCandidate}
        onClose={() => setOcrCandidate(undefined)}
      />
    </FormPageContainer>
  )
}
